import React, { useEffect, useMemo, useState } from 'react';
import { Card, Input, Button } from 'reactstrap';
import ReactTable from 'react-table';
import CustomModal from '../../../../../../../Components/Modals/CustomModal';
import UpdateDestinationCatAndAsinlistService from '../../../../../../../../Service/DashboardServices/ContentManagementServices/UpdateDestinationCatAndAsinlistService';
import CreateNewAssetSubmissionService from '../../../../../../../../Service/DashboardServices/ContentManagementServices/CreateNewAssetSubmissionService';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

const isImageFile = (file) => {
  const name = (file.actualFileName || '').toLowerCase();
  const ext = name.split('.').pop();
  if (IMAGE_EXTENSIONS.includes(ext)) return true;
  if (file.assetSubCategory === 7) return true;
  if (
    file.fileType &&
    IMAGE_EXTENSIONS.includes(file.fileType.split('/').pop().toLowerCase())
  ) {
    return true;
  }
  return false;
};

const sortCategories = (list) =>
  [...(list || [])].sort((a, b) =>
    (a.subCategoryName || '').localeCompare(b.subCategoryName || '')
  );

function SendAssetsApprovalPopup(props) {
  const [searchText, setSearchText] = useState('');
  const [destinationMappings, setDestinationMappings] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mode = props.mode || 'all';
  const headerTitle = props.headerTitle || 'Send Assets for Approval';

  const imageCategoryOptions = useMemo(
    () => sortCategories(props.filterSubcategoryList),
    [props.filterSubcategoryList]
  );

  const documentCategoryOptions = useMemo(
    () => sortCategories(props.filterProductSubcategoryList),
    [props.filterProductSubcategoryList]
  );

  const selectedDocumentIds = props.selectedDocumentIds || [];
  const selectedImageIds = props.selectedImageIds || [];
  const selectedAssetIds = props.selectedAssetIds || [
    ...selectedDocumentIds,
    ...selectedImageIds,
  ];

  const selectedAssetIdsKey = selectedAssetIds.join(',');
  const selectedDocumentIdsKey = selectedDocumentIds.join(',');
  const selectedImageIdsKey = selectedImageIds.join(',');

  const selectedDocuments = useMemo(() => {
    const source =
      props.documentList ||
      (mode === 'documents' ? props.fileList : []) ||
      [];
    const ids =
      mode === 'documents'
        ? selectedAssetIds
        : selectedDocumentIds.length
          ? selectedDocumentIds
          : selectedAssetIds;
    return (source || []).filter(
      (file) => ids.includes(file.id) && !isImageFile(file)
    );
  }, [
    props.documentList,
    props.fileList,
    mode,
    selectedAssetIdsKey,
    selectedDocumentIdsKey,
  ]);

  const selectedImages = useMemo(() => {
    const source =
      props.imageList ||
      (mode === 'graphics' ? props.fileList : []) ||
      [];
    const ids =
      mode === 'graphics'
        ? selectedAssetIds
        : selectedImageIds.length
          ? selectedImageIds
          : selectedAssetIds;
    return (source || []).filter(
      (file) => ids.includes(file.id) && isImageFile(file)
    );
  }, [
    props.imageList,
    props.fileList,
    mode,
    selectedAssetIdsKey,
    selectedImageIdsKey,
  ]);

  const allSelectedFiles = useMemo(() => {
    if (mode === 'documents') return selectedDocuments;
    if (mode === 'graphics') return selectedImages;
    return [...selectedDocuments, ...selectedImages];
  }, [mode, selectedDocuments, selectedImages]);

  useEffect(() => {
    if (!props.isOpen) return;

    const initialMappings = {};
    allSelectedFiles.forEach((file) => {
      const fileKey = String(file.id);
      initialMappings[fileKey] = file.destinationSubCategoriesId
        ? String(file.destinationSubCategoriesId)
        : '';
    });

    setDestinationMappings(initialMappings);
    setSearchText('');
    setRowErrors({});
    setFormError('');
  }, [props.isOpen, selectedAssetIdsKey, allSelectedFiles.length]);

  const searchedFiles = allSelectedFiles.filter(
    (file) =>
      (file.actualFileName || '')
        .toLowerCase()
        .includes(searchText.toLowerCase()) ||
      String(file.id).includes(searchText)
  );

  const getCategoryOptionsForFile = (file) =>
    isImageFile(file) ? imageCategoryOptions : documentCategoryOptions;

  const handleRowDestinationChange = (fileId, value) => {
    const fileKey = String(fileId);
    setDestinationMappings((prev) => ({
      ...prev,
      [fileKey]: value,
    }));
    setRowErrors((prev) => ({
      ...prev,
      [fileKey]: '',
    }));
    setFormError('');
  };

  const validateForm = () => {
    const nextRowErrors = {};
    let isValid = true;
    const missingCategoryMessage = 'Destination Category is mandatory for Partner Approval.';

    allSelectedFiles.forEach((file) => {
      const fileKey = String(file.id);
      const selectedCategory = destinationMappings[fileKey];
      if (!selectedCategory || selectedCategory === '') {
        nextRowErrors[fileKey] = missingCategoryMessage;
        isValid = false;
      }
    });

    setRowErrors(nextRowErrors);
    if (!isValid) {
      setFormError(missingCategoryMessage);
    } else {
      setFormError('');
    }
    return isValid;
  };

  const updateAssetDestination = (file) => {
    const data = {
      fileID: file.id,
      integerId: props.selectedProjectData.integerId,
      assetCategory: props.selectedSubCategories.assetCategory,
      assetIdentifier: props.selectedProjectData.brand,
      projectId: props.selectedProjectData.projectId,
      subCategoryId: props.selectedSubCategories.id,
      destinationSubCategories: destinationMappings[String(file.id)],
      asinList: file.asinList || '',
    };
    return UpdateDestinationCatAndAsinlistService(props.token, data);
  };

  const getDefaultSubmissionName = () => {
    if (props.submissionName) return props.submissionName;
    if (props.selectedASIN) return `Assets_${props.selectedASIN}`;
    return 'Other';
  };

  const handleSubmit = async () => {
    if (isSubmitting || !validateForm()) return;
    setIsSubmitting(true);

    try {
      const updateResults = await Promise.all(
        allSelectedFiles.map((file) => updateAssetDestination(file))
      );

      if (updateResults.some((result) => !result)) {
        setIsSubmitting(false);
        return;
      }

      const submissionData = {
        imgList: allSelectedFiles.map((file) => file.id),
        submissionName: getDefaultSubmissionName(),
        submissionFor: props.selectedASIN ? props.selectedASIN : '',
        integerId: props.selectedProjectData.integerId,
        assetCategory: props.selectedSubCategories.assetCategory,
        assetIdentifier: props.selectedProjectData.brand,
        projectId: props.selectedProjectData.projectId,
        subCategoryId: props.selectedSubCategories.id,
        comment: props.submissionNote,
      };

      const res = await CreateNewAssetSubmissionService(
        submissionData,
        props.token
      );

      if (res) {
        props.onSubmitSuccess(res);
        props.toggle();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    {
      Header: 'Asset Name',
      accessor: 'actualFileName',
      Cell: (rowInfo) => {
        const file = rowInfo.original;
        const isImage = isImageFile(file);
        return (
          <div className="d-flex align-items-center">
            {isImage && file.filePath && (
              <img
                src={file.filePath}
                alt={file.actualFileName}
                style={{
                  width: 36,
                  height: 36,
                  objectFit: 'contain',
                  marginRight: 8,
                  borderRadius: 4,
                }}
              />
            )}
            <p title={rowInfo.value} className="m-0 comment-paragraph">
              {rowInfo.value}
            </p>
          </div>
        );
      },
    },
    {
      Header: 'Destination Category',
      accessor: 'id',
      width: 320,
      Cell: (rowInfo) => {
        const file = rowInfo.original;
        const fileKey = String(file.id);
        const categoryOptions = getCategoryOptionsForFile(file);
        return (
          <div>
            <Input
              type="select"
              value={destinationMappings[fileKey] || ''}
              onChange={(e) =>
                handleRowDestinationChange(fileKey, e.target.value)
              }
              className={rowErrors[fileKey] ? 'is-invalid' : ''}
              bsSize="sm"
            >
              <option value="">--Destination Category--</option>
              {(categoryOptions || []).map((subCategory) => (
                <option key={subCategory.id} value={String(subCategory.id)}>
                  {subCategory.subCategoryName}
                  {' '}
                  ({subCategory.assetCategory__categoryName})
                </option>
              ))}
            </Input>
            {rowErrors[fileKey] && (
              <small className="text-danger d-block mt-1">{rowErrors[fileKey]}</small>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <CustomModal
      isOpen={props.isOpen}
      toggle={props.toggle}
      className="curve-popup"
      size="xl"
      header={
        <h5 className="mb-0">
          {headerTitle}
          {props.selectedASIN ? ` - ${props.selectedASIN}` : ''}
        </h5>
      }
    >
      <Card className="card-of-problemarea p-3">
        <div className="d-flex align-items-center justify-content-between">
          <h5 className="mb-0">
            <b>Selected Assets</b>
            <small className="text-muted ml-2">({allSelectedFiles.length})</small>
          </h5>
          <Input
            type="text"
            placeholder="Search"
            className="w-25"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        {formError && (
          <div className="alert alert-danger py-2 px-3 mt-2 mb-0" role="alert">
            {formError}
          </div>
        )}

        <ReactTable
          className="-fixed -highlight -striped mt-2"
          data={searchedFiles}
          columns={columns}
          defaultPageSize={5}
          noDataText="No selected assets found..."
          getTheadProps={() => ({
            style: {
              overflow: 'hidden',
              background: '#B1FA63',
              color: '#243837',
            },
          })}
          getTrProps={() => ({
            style: {
              minHeight: '45px',
            },
          })}
        />

        <div className="d-flex justify-content-end mt-3 pt-3 border-top">
          <Button
            className="btn-background-color"
            style={{ minWidth: '140px' }}
            disabled={isSubmitting || allSelectedFiles.length === 0}
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </Card>
    </CustomModal>
  );
}

export default SendAssetsApprovalPopup;
